/**
 * VaultAccessGrantSheet — host-rendered pick-and-consent flow behind the
 * `vault_documents_pick` bridge op (the purchase-confirmation-sheet pattern
 * applied to user data; sanctioned transport exception, see
 * docs/capability-placement.md § "Transport exceptions").
 *
 * Two steps, both host chrome the plugin can neither see nor script:
 *   1. the shared Vault picker (multi-select; uploads off; proprietary
 *      documents filtered out — the R2 mirror, also enforced server-side),
 *   2. an explicit consent confirmation that is HONEST about what a grant
 *      means: standing access until revoked, apps can copy what you share,
 *      access is logged, revocation lets in-flight links live out ≤1 h.
 *
 * Confirm creates the per-document grants via the host session
 * (POST /v1/vault/consents/arche — the plugin can never mint its own), then
 * resolves the plugin's promise with picked-document METADATA only; content
 * flows through the audited consented-read entity verbs afterwards.
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, FileText, ShieldCheck } from 'lucide-react';
import { VaultFilePickerDialog, type VaultPickerResult } from '@/components/VaultFilePickerDialog';

export interface VaultPickResolvedDocument {
  documentId: string;
  name: string;
  mimeType: string | null;
  sizeBytes: number | null;
}

export type VaultPickResolution = { canceled: true } | { documents: VaultPickResolvedDocument[] };

interface VaultAccessGrantSheetProps {
  open: boolean;
  /** The plugin arche requesting access. */
  archeId: string;
  /** Display name for the consent copy; fetched by the parent when known. */
  archeName: string | null;
  maxDocuments: number;
  onResolve: (result: VaultPickResolution) => void;
}

export function VaultAccessGrantSheet({
  open,
  archeId,
  archeName,
  maxDocuments,
  onResolve,
}: VaultAccessGrantSheetProps) {
  const [step, setStep] = useState<'pick' | 'confirm'>('pick');
  const [selected, setSelected] = useState<VaultPickerResult[]>([]);
  const [granting, setGranting] = useState(false);
  const [grantError, setGrantError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setStep('pick');
      setSelected([]);
      setGranting(false);
      setGrantError(null);
    }
  }, [open]);

  const appLabel = archeName ?? 'This app';

  const handleFilesSelected = useCallback((files: VaultPickerResult[]) => {
    setSelected(files);
    setStep('confirm');
  }, []);

  const handleCancel = useCallback(() => {
    onResolve({ canceled: true });
  }, [onResolve]);

  const handleConfirm = useCallback(async () => {
    setGranting(true);
    setGrantError(null);
    try {
      await api.post('/vault/consents/arche', {
        granteeArcheId: archeId,
        documentIds: selected.slice(0, maxDocuments).map((f) => f.documentId),
      });
      onResolve({
        documents: selected.slice(0, maxDocuments).map((f) => ({
          documentId: f.documentId,
          name: f.fileName,
          mimeType: f.mimeType ?? null,
          sizeBytes: f.sizeBytes ?? null,
        })),
      });
    } catch (err) {
      const error = err as Error & { code?: string };
      setGrantError(
        error.code === 'SENSITIVITY_FORBIDDEN_FOR_ARCHE_GRANT'
          ? 'One of the selected documents is marked Proprietary — proprietary documents cannot be shared with apps. Go back and deselect it.'
          : (error.message ?? 'Could not grant access. Please try again.'),
      );
    } finally {
      setGranting(false);
    }
  }, [archeId, maxDocuments, onResolve, selected]);

  // Render nothing until a pick request is active — the sheet sits inside
  // every PluginRenderer mount, and an always-mounted picker dialog would run
  // its queries (and evaluate its imports) for every plugin view with no
  // pick in flight. State is re-initialized by the `open` effect above.
  if (!open) return null;

  return (
    <>
      <VaultFilePickerDialog
        open={open && step === 'pick'}
        onOpenChange={(o) => {
          if (!o) handleCancel();
        }}
        multiSelect
        onFilesSelected={handleFilesSelected}
        title={`Share documents with ${appLabel}`}
        description={`Pick up to ${maxDocuments} document${maxDocuments !== 1 ? 's' : ''} from your Vault. ${appLabel} will only be able to read what you pick here.`}
        confirmLabel="Continue"
        // Uploads are for the user's own vault flows, not an app's ask —
        // the consent decision should be about EXISTING documents.
        allowUpload={false}
        // R2 mirror: proprietary documents are never grantable to apps.
        // Server-side the grant route + the read path both refuse them;
        // hiding them here keeps the sheet honest instead of error-driven.
        mimeFilter={(doc) => doc.sensitivity !== 'proprietary'}
      />

      <Dialog
        open={open && step === 'confirm'}
        onOpenChange={(o) => {
          if (!o && !granting) handleCancel();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              Allow {appLabel} to read {selected.length} document
              {selected.length !== 1 ? 's' : ''}?
            </DialogTitle>
            <DialogDescription>
              {appLabel} will be able to read these documents until you remove access in Vault → App
              Access. Apps can copy or store content you share with them. Every access is logged.
            </DialogDescription>
          </DialogHeader>

          <ul className="max-h-48 overflow-y-auto space-y-1.5 rounded-md border p-3">
            {selected.map((file) => (
              <li key={file.documentId} className="flex items-center gap-2 text-sm">
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate">{file.fileName}</span>
              </li>
            ))}
          </ul>

          <p className="text-xs text-muted-foreground">
            Removing access stops new reads immediately. Download links issued in the last hour may
            keep working until they expire.
          </p>

          {grantError && <p className="text-sm text-destructive">{grantError}</p>}

          <DialogFooter>
            <Button variant="outline" onClick={() => setStep('pick')} disabled={granting}>
              Back
            </Button>
            <Button variant="outline" onClick={handleCancel} disabled={granting}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={granting || selected.length === 0}>
              {granting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Allow access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
